import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Globe, FileText, MousePointerClick, Zap, TrendingUp,
  ArrowUpRight, Plus, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { dashboardAPI, seedAPI } from '@/lib/api';
import { formatDate, statusBadgeClass, statusLabel } from '@/lib/utils';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

const FadeUp = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.2, ease: 'easeOut', delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const KPICard = ({ title, value, icon: Icon, trend, delay, testId }) => (
  <FadeUp delay={delay}>
    <Card className="relative card-hover border-border/70 overflow-hidden" data-testid={testId}>
      <div className="kpi-accent" />
      <CardContent className="pt-5 pb-4 px-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mb-2">{title}</p>
            <p className="text-2xl sm:text-3xl font-semibold tabular-nums font-['Space_Grotesk']">{value}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-secondary/60 flex items-center justify-center">
            <Icon className="w-5 h-5 text-muted-foreground" />
          </div>
        </div>
        {trend && (
          <div className="flex items-center gap-1 mt-2 text-xs text-emerald-400">
            <TrendingUp className="w-3 h-3" />
            <span>{trend}</span>
          </div>
        )}
      </CardContent>
    </Card>
  </FadeUp>
);

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await dashboardAPI.stats();
      setStats(data);
    } catch (err) {
      console.error('Dashboard stats error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleSeed = async () => {
    try {
      const { data } = await seedAPI.create();
      toast.success(data.message);
      loadStats();
    } catch (err) {
      toast.error('Seed olusturulamadi');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="h-[120px] rounded-xl shimmer" />
          ))}
        </div>
        <div className="h-[300px] rounded-xl shimmer" />
      </div>
    );
  }

  const chartData = stats?.click_stats?.length > 0 ? stats.click_stats : [
    { date: 'Pzt', clicks: 0 },
    { date: 'Sal', clicks: 0 },
    { date: 'Car', clicks: 0 },
    { date: 'Per', clicks: 0 },
    { date: 'Cum', clicks: 0 },
    { date: 'Cmt', clicks: 0 },
    { date: 'Paz', clicks: 0 },
  ];

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KPICard title="Toplam Site" value={stats?.total_sites || 0} icon={Globe} delay={0} testId="dashboard-kpi-total-sites" />
        <KPICard title="Aktif Siteler" value={stats?.active_sites || 0} icon={Zap} delay={0.05} testId="dashboard-kpi-active-sites" />
        <KPICard title="Toplam Sayfa" value={stats?.total_pages || 0} icon={FileText} delay={0.1} testId="dashboard-kpi-total-pages" />
        <KPICard title="CTA Tiklamalari" value={stats?.total_clicks || 0} icon={MousePointerClick} delay={0.15} testId="dashboard-kpi-clicks" />
      </div>

      {/* Charts & Recent */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <FadeUp delay={0.2} className="xl:col-span-8">
          <Card className="border-border/70">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium">Haftalik Tiklamalar</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.15} />
                        <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="rgba(167,176,190,0.1)" vertical={false} />
                    <XAxis dataKey="date" tick={{ fill: '#A7B0BE', fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: '#A7B0BE', fontSize: 12 }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ background: '#12151A', border: '1px solid #262C35', borderRadius: 8, color: '#F2F4F7' }}
                    />
                    <Area type="monotone" dataKey="clicks" stroke="#D4AF37" strokeWidth={2} fill="url(#goldGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </FadeUp>

        <FadeUp delay={0.25} className="xl:col-span-4">
          <Card className="border-border/70">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-medium">Son Siteler</CardTitle>
              <Link to="/sites">
                <Button variant="ghost" size="sm" className="text-xs text-muted-foreground">
                  Tumunu Gor <ArrowUpRight className="w-3 h-3 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {stats?.recent_sites?.length > 0 ? (
                <div className="space-y-3">
                  {stats.recent_sites.map((site) => (
                    <Link
                      key={site.id}
                      to={`/sites/${site.id}/detail`}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-secondary/30 transition-colors duration-150"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{site.brand_name}</p>
                        <p className="text-xs text-muted-foreground font-mono truncate">{site.domain}</p>
                      </div>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${statusBadgeClass(site.status)}`}>
                        {statusLabel(site.status)}
                      </span>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Globe className="w-10 h-10 text-muted-foreground/40 mb-3" />
                  <p className="text-sm text-muted-foreground mb-3">Henuz site yok</p>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleSeed} variant="outline" className="text-xs">
                      <RefreshCw className="w-3 h-3 mr-1" /> Seed Olustur
                    </Button>
                    <Link to="/sites/new">
                      <Button size="sm" className="text-xs bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
                        <Plus className="w-3 h-3 mr-1" /> Yeni Site
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </FadeUp>
      </div>
    </div>
  );
}
